// 맵 데이터(소유: 오케스트레이터). docs/TECH.md 5장: 배치는 코드·데이터, 충돌·스폰은 이 데이터에서 나온다.
// 좌표: y 위, 바닥 XZ 평면, 단위 m. 북쪽(화면 위) = -z. 모든 사각형 좌표는 1m 격자 정수.

import { TOWN_MAPS } from './villageMaps';

export interface Rect {
  readonly x0: number;
  readonly z0: number;
  readonly x1: number;
  readonly z1: number;
}

export interface WaveDef {
  /** [고블린, 방패 고블린, 멧돼지, (주술사)] 마릿수. R1은 3칸, 4번째(MobKind.Shaman)가 없으면 0 */
  readonly counts: readonly [number, number, number] | readonly [number, number, number, number];
  /** 엘리트로 승격할 마릿수(앞에서부터) */
  readonly elites?: number;
}

/** 던전 지역. 마을에서 고른다(docs/HUNT-R2.md). r3·r4는 D3a(서리 묘지·가라앉은 사원), r5·r6은 3개월 C2(황금 모래 폐도·심연의 성채) */
export type RegionId = 'r1' | 'r2' | 'r3' | 'r4' | 'r5' | 'r6';
export type MapId = 'village' | RegionId;
export const REGION_IDS: readonly RegionId[] = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6'];
export function isRegionId(v: unknown): v is RegionId { return typeof v === 'string' && (REGION_IDS as readonly string[]).includes(v); }
/** 지역 번호(1..) → 지도 id. 지도가 없는 지역은 null */
export function regionIdOf(no: number): RegionId | null { return no >= 1 && no <= REGION_IDS.length ? REGION_IDS[no - 1] : null; }
/** 지도 id → 지역 번호(1..) */
export function regionNoOf(id: RegionId): number { return REGION_IDS.indexOf(id) + 1; }

/**
 * 보스방 보스 종류(D3a 보스 정의 content/bosses.ts BOSS_DEFS의 키). treant = R1 고목 수호자, ember = R2 잿불 대주술사(아레나 리더).
 * 중간보스(n-5): chieftain 고블린 족장 · golem 용암 골렘 · warden 묘지기 기사 · priest 독안개 사제.
 * 지역보스(n-10): treant · ember · lich 서리 리치 · hydra 늪의 히드라.
 * 지역5·6(C2): captain 경비대장 · sandlord 모래 군주 · executioner 심연 집행자 · abysslord 심연의 군주.
 */
export type BossKey = 'treant' | 'ember' | 'chieftain' | 'golem' | 'warden' | 'lich' | 'priest' | 'hydra' | 'captain' | 'sandlord' | 'executioner' | 'abysslord';
/**
 * 지역 위험 요소 테마: 웅덩이(lava 칸)·분출구(vents)의 이름·효과·색. ember = 용암 화상, frost = 서리 균열(동상 + 둔화), venom = 독 늪(중독),
 * sand = 유사(流沙, 빠지면 둔화 + 모래 찰과상, 분출구 = 모래 간헐천), void = 심연 균열(침식: 중독처럼 시간 피해, 분출구 = 공허 기둥)
 */
export type HazardTheme = 'ember' | 'frost' | 'venom' | 'sand' | 'void';

export interface RoomDef {
  readonly name: string;
  readonly kind: 'battle' | 'boss';
  /** 방 바닥 영역(floors에도 같은 사각형이 있다) */
  readonly area: Rect;
  /** 이 방으로 들어오는 문(map.gates 인덱스). 입장하면 닫혀 전투 구역이 잠긴다 */
  readonly gateIn: number;
  /** 다음으로 나가는 문. 방을 정리하면 열린다. 없으면 -1 */
  readonly gateOut: number;
  readonly waves: readonly WaveDef[];
  /** 보스 등장 위치(boss 방) */
  readonly bossAt?: { readonly x: number; readonly z: number };
  /** 보스 종류(boss 방). 없으면 treant */
  readonly boss?: BossKey;
  /**
   * 후열 구역(R2): 원거리 몹(주술사)은 이 사각형 안의 걸을 수 있는 점에서 나와 이 안을 지킨다.
   * 없으면 다른 몹과 같은 입구에서 나온다.
   */
  readonly backline?: Rect;
  /**
   * 웨이브 입구 후보(S2): 방 둘레가 사각형이 아닌 원형·굽은 방에서 몹이 나오는 자리. 없으면 area 둘레를 고르게 나눈 점에서 고른다.
   * 걸을 수 있는 칸이어야 한다(levels 테스트가 확인).
   */
  readonly entries?: readonly { readonly x: number; readonly z: number }[];
  /**
   * 방 대표 지점(S2): 길 안내 표식·QA 카메라가 가리키는 걸을 수 있는 칸. 방 가운데가 구멍·못·섬일 수 있어 area 가운데 대신 쓴다.
   * 없으면 area 가운데.
   */
  readonly focus?: { readonly x: number; readonly z: number };
}

/**
 * 용암 분출구(R2). 방 전투 중에만 순환하며, 경고 원(텔레그래프)과 판정이 같은 원이다.
 * offset: 이 방 순환의 첫 분출까지 추가 지연(틱 비율 0..1, HAZARD.ventPeriod에 곱한다).
 * fromBossHp: 아레나 분출구는 보스 체력 비율이 이 값 이하가 되어야 깨어난다.
 */
export interface VentDef {
  readonly x: number;
  readonly z: number;
  readonly radius: number;
  /** map.rooms 인덱스 */
  readonly room: number;
  readonly offset: number;
  readonly fromBossHp?: number;
}

export interface GateDef {
  readonly rect: Rect;
  readonly openAtStart: boolean;
}

export type LandmarkKind = 'fountain' | 'shop' | 'forge' | 'trainer' | 'board' | 'gate' | 'pillar' | 'rubble' | 'statue' | 'tree' | 'stall' | 'decor' | 'basalt'
  /** S2 스테이지 지도: 망루 밑동 · 모닥불 · 뼈 왕좌 · 뿌리 매듭(충돌 사각형, 모양은 렌더의 세트피스·키트가 정한다) */
  | 'tower' | 'bonfire' | 'throne' | 'root'
  /** S7 마을②~④: 보석상·연금술사 건물(시설 이름표), 바닥 위 키트 소품의 1칸 충돌(그리지 않는다, shared/villageMaps 소품 목록이 그린다) */
  | 'jeweler' | 'alchemist' | 'prop'
  /** S6 지역2 스테이지 지도: 단조장 모루 · 불의 제단 · 거대 도가니(충돌 사각형, 모양은 세트피스) */
  | 'anvil' | 'altar' | 'crucible';
export interface Landmark {
  readonly kind: LandmarkKind;
  readonly label?: string;
  readonly rect: Rect;
  /** 그레이박스 높이(m) */
  readonly height: number;
  /** 충돌 여부 */
  readonly solid: boolean;
}

export interface ExitDef {
  readonly rect: Rect;
  readonly to: MapId;
  readonly label: string;
}

export interface MapDef {
  readonly id: MapId;
  readonly name: string;
  readonly floors: readonly Rect[];
  readonly landmarks: readonly Landmark[];
  readonly gates: readonly GateDef[];
  readonly rooms: readonly RoomDef[];
  readonly exits: readonly ExitDef[];
  readonly spawn: { readonly x: number; readonly z: number; readonly facing: number };
  /** 벽 그레이박스 높이(m) */
  readonly wallHeight: number;
  /**
   * 낭떠러지(R2, 렌더 전용): 바닥이 아닌 이 사각형 칸은 벽 대신 깊은 용암 협곡으로 그린다.
   * 게임 판정은 다른 빈칸(Void)과 같다(떨어지는 판정 없음, 못 지나간다).
   */
  readonly chasms?: readonly Rect[];
  /** 용암 웅덩이(R2): 바닥 높이. 영웅은 지나갈 수 있지만 화상(HAZARD.lava*)을 입고, 몹·보스는 못 들어간다 */
  readonly lava?: readonly Rect[];
  readonly vents?: readonly VentDef[];
  /** 웅덩이·분출구 테마(없으면 ember). 웅덩이 칸 효과와 경고 색이 바뀐다 */
  readonly hazardTheme?: HazardTheme;
  /**
   * 세계 정수 틱 예약표(ThreatClock)를 쓰는 지도: 분출구·원거리 몹 포격·주술사형 보스의 경고가 같은 틱에 겹쳐 터지지 않게 예약한다.
   * R2부터 켠다(R1은 예약 없이 기존 판정 그대로)
   */
  readonly threatClock?: boolean;
  /**
   * 스테이지 지도 변형(S2). 있으면 id는 지역 id 그대로(r1)이고 이 키가 지도 한 장을 가른다. 없으면 기존 지역 지도(자유 입장).
   * 판정 격자·렌더 캐시는 지도 객체 단위라 키가 달라도 서로 섞이지 않는다.
   */
  readonly stageKey?: StageMapKey;
  /** 좌우 반전 변형인지(a2·b2) */
  readonly mirrored?: boolean;
  /** 시간대(없으면 지역 기본 분위기) */
  readonly timeOfDay?: TimeOfDay;
  /** 가장자리 기본 모양(없으면 cliff)과 구역별 덮어쓰기(렌더 전용). 스테이지 지도는 벽 대신 낭떠러지·물이 기본이다 */
  readonly edgeStyle?: EdgeStyle;
  readonly edgeZones?: readonly EdgeZone[];
  /** 수면(렌더 전용): 이 사각형 안의 빈칸은 낭떠러지 대신 얕은 물로 그린다 */
  readonly water?: readonly Rect[];
  /** 큰 세트피스 앵커(렌더 전용 데이터) */
  readonly sets?: readonly SetPieceDef[];
  /** 빈칸 아래 용암 면 높이(m, 렌더 전용, S6 지역2). 있으면 낭떠러지가 이 높이의 용암 바다까지 내려간다 */
  readonly seaY?: number;
  /**
   * 환경 발광 예산(렌더 전용, C8-7 레이드). 보스전 배경이 보스와 시선을 다투지 않게 줄이는 배율.
   * sets = 세트피스 발광 지도(기둥 균열·횃불 그릇), seaFar = 섬 가장자리에서 먼 용암 바다(가장자리 위험 테는 그대로).
   */
  readonly envGlow?: { readonly sets?: number; readonly seaFar?: number };
}

/**
 * 지도 가장자리(바닥과 빈칸 사이) 모양(S2 스테이지 지도, 렌더 전용): 낭떠러지 돌 테두리 · 낮은 난간 · 목책 · 뿌리 · 수로 둑 · 없음(다리가 자기 난간을 가진 곳).
 * S6 지역2: basalt 현무암 기둥 테두리 · chain 쇠사슬 난간(능선·둑길) · forge 단조장 쇠 울타리
 */
export type EdgeStyle = 'cliff' | 'parapet' | 'palisade' | 'roots' | 'water' | 'bare' | 'basalt' | 'chain' | 'forge';
export interface EdgeZone {
  readonly rect: Rect;
  readonly style: EdgeStyle;
}
/**
 * 세트피스 종류(Blender GLB public/assets/r1/set_<종류>.glb, waterfall·lavafall만 코드로 만든다).
 * e_ 로 시작하는 것은 S6 지역2(잿불 협곡) 조각이다: public/assets/r2/set_<e_ 뺀 이름>.glb(tools/blender/r2_stage_sets.py)
 */
export type SetPieceKind =
  | 'bridge' | 'bridge_wood' | 'bridge_root' | 'ruin_arch' | 'ruin_arcade' | 'tower' | 'throne' | 'palisade_gate' | 'root_gate'
  | 'bonfire' | 'banner_pole' | 'bone_pile' | 'far_cliff' | 'far_aqueduct' | 'waterfall' | 'root_arc' | 'root_knot'
  | 'lavafall' | 'e_bridge' | 'e_gate' | 'e_brazier' | 'e_fire_bowl' | 'e_banner' | 'e_chain_span' | 'e_crucible' | 'e_anvil' | 'e_altar'
  | 'e_obelisk' | 'e_basalt' | 'e_rubble' | 'e_far_spire' | 'e_far_aqueduct' | 'e_prop_crystal';
export interface SetPieceDef {
  readonly kind: SetPieceKind;
  readonly x: number;
  readonly z: number;
  /** Y축 회전(rad, 0 = 정면이 +z) */
  readonly rot: number;
  /** 균일 배율(기본 1) */
  readonly scale?: number;
  /** 다리 길이(m, 다리 종류만): 모델 길이 방향(로컬 z)만 이 길이로 늘린다. 다리 바닥은 이 사각형의 지면 윗면을 대신한다 */
  readonly len?: number;
  /** 바닥 위 높이(m, 기본 0). 원경은 낭떠러지 아래로 내린다 */
  readonly y?: number;
}
/** 스테이지 지도 변형(S2·S6): a·a2 = 지역 전반 · b·b2 = 후반 · m = 중간보스 아레나 · f = 지역보스 아레나. a2·b2는 좌우 반전 + 다른 시간대. 지역은 MapDef.id가 가른다 */
export type StageMapKey = 'a' | 'a2' | 'b' | 'b2' | 'm' | 'f';
/** 시간대: 분위기(mood) 변형을 고른다 */
export type TimeOfDay = 'morning' | 'dusk';

const r = (x0: number, z0: number, x1: number, z1: number): Rect => ({ x0, z0, x1, z1 });
const N = Math.PI; // facing: 북쪽(-z)

// ── 마을 광장 ────────────────────────────────────────
export const VILLAGE: MapDef = {
  id: 'village',
  name: '바람결 마을 광장',
  floors: [r(-18, -16, 18, 16), r(-3, -26, 3, -16), r(8, -26, 13, -16)],
  landmarks: [
    // 광장 중심 표식(콘셉트 v-overview처럼 크게): 지름 6m
    { kind: 'fountain', label: '분수', rect: r(-3, -3, 3, 3), height: 1.4, solid: true },
    { kind: 'shop', label: '상점', rect: r(-17, -12, -10, -5), height: 5, solid: true },
    { kind: 'forge', label: '대장간', rect: r(10, -12, 17, -5), height: 5, solid: true },
    { kind: 'trainer', label: '교관', rect: r(-17, 6, -11, 12), height: 4, solid: true },
    { kind: 'board', label: '의뢰 게시판', rect: r(12, 7, 15, 8), height: 2.4, solid: true },
    { kind: 'gate', label: '던전 입구', rect: r(-4, -27, 4, -26), height: 6, solid: true },
    { kind: 'tree', rect: r(-16, 13, -14, 15), height: 6, solid: true },
    { kind: 'tree', rect: r(14, 13, 16, 15), height: 6, solid: true },
    // 장터 골목(콘셉트 style-B-village): 중앙 통로 양옆 노점 줄. 노점·뒤 깃대·나무상자를 한 사각형으로 막는다.
    // 상점·대장간 문 앞(z -11..-6)과 교관 문 앞(x -11..-8, z 7..11)은 비운다.
    // 장터 골목을 중앙 통로 바로 옆(x ±5~8)으로 당긴다(콘셉트처럼 길이 좁고 노점이 영웅 곁에 붙는다).
    // 왼쪽 z 7~11은 교관 입구로 가는 틈으로 비운다. 분수 옆 통로 x 3.45~4.55는 비운다(영웅 반경 0.45).
    { kind: 'stall', rect: r(-8, -3, -5, 1), height: 2.4, solid: true },
    { kind: 'stall', rect: r(-8, 3, -5, 7), height: 2.4, solid: true },
    { kind: 'stall', rect: r(-8, 11, -5, 15), height: 2.4, solid: true },
    { kind: 'stall', rect: r(5, -3, 8, 1), height: 2.4, solid: true },
    { kind: 'stall', rect: r(5, 3, 8, 7), height: 2.4, solid: true },
    { kind: 'stall', rect: r(5, 9, 8, 13), height: 2.4, solid: true },
    // 카메라 앞 전경(콘셉트처럼 화면 아래 모서리를 가로등·깃발·꽃 상자가 채운다). 스폰(0,10) 남쪽이라 동선을 막지 않는다
    { kind: 'decor', rect: r(-5, 12, -4, 14), height: 3.6, solid: true },
    { kind: 'decor', rect: r(4, 12, 5, 14), height: 3.6, solid: true },
    // R2 잿불 협곡 문(2026-10-15 새 사냥터): 이끼 유적 문 오른쪽에 나란히 둔다. 스폰에서 북쪽을 보면 두 문이 함께 보인다.
    { kind: 'gate', label: '잿불 협곡', rect: r(7, -27, 14, -26), height: 6, solid: true },
  ],
  gates: [],
  rooms: [],
  // 순서 고정: exits[0] = R1(기존 하네스·테스트가 북쪽 문으로 걸어 들어간다), exits[1] = R2
  exits: [
    { rect: r(-3, -26, 3, -23), to: 'r1', label: '이끼 유적' },
    { rect: r(8, -26, 13, -23), to: 'r2', label: '잿불 협곡' },
  ],
  spawn: { x: 0, z: 10, facing: N },
  wallHeight: 2.5,
};

// ── R1 이끼 유적 던전: 입구 통로 → 전투방 3 → 보스방 ───────────
// 문은 통로 쪽 1m 줄. 방에 들어가면 gateIn이 닫히고, 정리하면 gateOut이 열린다.
export const R1: MapDef = {
  id: 'r1',
  name: '이끼 유적',
  floors: [
    r(-4, -12, 4, 4), // 입구 통로
    r(-14, -38, 14, -12), // 전투방 1
    r(-3, -50, 3, -38), // 통로 2
    r(-16, -78, 16, -50), // 전투방 2
    r(-3, -90, 3, -78), // 통로 3
    r(-17, -120, 17, -90), // 전투방 3
    r(-3, -132, 3, -120), // 통로 4
    r(-20, -170, 20, -132), // 보스방
  ],
  landmarks: [
    { kind: 'statue', label: '이끼 여신상', rect: r(-3, 2, 3, 4), height: 4, solid: true },
    { kind: 'pillar', rect: r(-10, -18, -8, -16), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(8, -18, 10, -16), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(-10, -34, -8, -32), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(8, -34, 10, -32), height: 4.5, solid: true },
    { kind: 'rubble', rect: r(-13, -60, -10, -57), height: 1.2, solid: true },
    { kind: 'rubble', rect: r(10, -71, 13, -68), height: 1.2, solid: true },
    { kind: 'pillar', rect: r(-1, -65, 1, -63), height: 4.5, solid: true },
    // 방3 기둥: 전투 카메라(방 가운데에서 좌우 약 11m)가 화면 양옆에 폐허 기둥을 담도록 x ±9 중심
    { kind: 'pillar', rect: r(-10, -98, -8, -96), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(8, -98, 10, -96), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(-10, -114, -8, -112), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(8, -114, 10, -112), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(-16, -140, -14, -138), height: 6, solid: true },
    { kind: 'pillar', rect: r(14, -140, 16, -138), height: 6, solid: true },
    { kind: 'pillar', rect: r(-16, -166, -14, -164), height: 6, solid: true },
    { kind: 'pillar', rect: r(14, -166, 16, -164), height: 6, solid: true },
  ],
  gates: [
    { rect: r(-4, -12, 4, -11), openAtStart: true }, // 0: 방1 입구
    { rect: r(-3, -39, 3, -38), openAtStart: false }, // 1: 방1 출구
    { rect: r(-3, -50, 3, -49), openAtStart: true }, // 2: 방2 입구
    { rect: r(-3, -79, 3, -78), openAtStart: false }, // 3: 방2 출구
    { rect: r(-3, -90, 3, -89), openAtStart: true }, // 4: 방3 입구
    { rect: r(-3, -121, 3, -120), openAtStart: false }, // 5: 방3 출구
    { rect: r(-3, -132, 3, -131), openAtStart: true }, // 6: 보스방 입구
  ],
  rooms: [
    // 화면 동시 몹 25~40(COMBAT-FEEL 3장): 다음 웨이브는 잔존 30% 이하에서 겹쳐 나오므로 방2·방3 후반에 25~40마리가 모인다
    { name: '1-1 무너진 회랑', kind: 'battle', area: r(-14, -38, 14, -12), gateIn: 0, gateOut: 1, waves: [{ counts: [16, 0, 0] }, { counts: [12, 3, 0] }] },
    { name: '1-2 이끼 정원', kind: 'battle', area: r(-16, -78, 16, -50), gateIn: 2, gateOut: 3, waves: [{ counts: [18, 3, 0] }, { counts: [14, 5, 2] }, { counts: [16, 2, 3], elites: 1 }] },
    { name: '1-3 성채로 가는 길', kind: 'battle', area: r(-17, -120, 17, -90), gateIn: 4, gateOut: 5, waves: [{ counts: [26, 0, 0] }, { counts: [18, 6, 2] }, { counts: [24, 6, 4], elites: 2 }] },
    { name: '1-보스 고목 수호자', kind: 'boss', area: r(-20, -170, 20, -132), gateIn: 6, gateOut: -1, waves: [], bossAt: { x: 0, z: -158 } },
  ],
  exits: [],
  spawn: { x: 0, z: 0, facing: N },
  wallHeight: 3.2,
};

// ── R2 잿불 협곡: 바위턱 → 능선 → 전투방 2 → 아레나 (docs/HUNT-R2.md) ──────
// R1의 일직선 북진과 달리 능선을 타고 중앙 → 북서 → 북동으로 꺾인다. 능선 양옆은 낭떠러지(chasms, 렌더 전용).
// 방1은 동서로 가로지른 용암 골(낭떠러지)이 남·북 단을 나누고 다리 둘로만 건넌다. 주술사는 북쪽 단(후열)에서 포격한다.
// 방2는 가운데 분화구 웅덩이(영웅만 지나갈 수 있고 화상)와 그 둘레 분출구 셋. 아레나는 리더 + 체력 60%부터 깨어나는 분출구 넷.
// 바닥 판정은 평면이다(고저감은 렌더의 낭떠러지 깊이·절벽 높이로만 준다).
export const R2: MapDef = {
  id: 'r2',
  name: '잿불 협곡',
  floors: [
    r(-5, -4, 5, 5), // 입구 바위턱
    r(-2, -14, 2, -4), // 능선 1(폭 4m)
    r(-18, -24, 18, -14), // 방1 남쪽 단
    r(-12, -27, -9, -24), // 방1 서쪽 다리
    r(9, -27, 12, -24), // 방1 동쪽 다리
    r(-18, -38, 18, -27), // 방1 북쪽 단(후열)
    r(-16, -48, -12, -38), // 능선 2(북서)
    r(-24, -80, 8, -48), // 방2 분화구 계단
    r(2, -92, 6, -80), // 능선 3(북동)
    r(-14, -124, 22, -92), // 아레나
  ],
  landmarks: [
    { kind: 'statue', label: '잿불 수문장', rect: r(-3, 3, 3, 5), height: 4.5, solid: true },
    // 방1: 남쪽 단 양 끝 현무암 기둥, 북쪽 단 가운데 엄폐 기둥 둘
    { kind: 'basalt', rect: r(-16, -18, -14, -16), height: 5, solid: true },
    { kind: 'basalt', rect: r(14, -18, 16, -16), height: 5, solid: true },
    { kind: 'basalt', rect: r(-6, -34, -4, -32), height: 4, solid: true },
    { kind: 'basalt', rect: r(4, -34, 6, -32), height: 4, solid: true },
    // 방2: 서쪽 벽 기둥, 동쪽 단 기둥, 후열 낮은 잔해
    { kind: 'basalt', rect: r(-22, -57, -20, -55), height: 5, solid: true },
    { kind: 'basalt', rect: r(2, -58, 4, -56), height: 5, solid: true },
    { kind: 'rubble', rect: r(-5, -76, -2, -74), height: 1.2, solid: true },
    // 아레나: 북쪽 모서리 기둥 둘(리더 뒤 실루엣)
    { kind: 'basalt', rect: r(-11, -121, -9, -119), height: 7, solid: true },
    { kind: 'basalt', rect: r(17, -121, 19, -119), height: 7, solid: true },
  ],
  gates: [
    { rect: r(-2, -14, 2, -13), openAtStart: true }, // 0: 방1 입구(능선 1 끝)
    { rect: r(-16, -39, -12, -38), openAtStart: false }, // 1: 방1 출구(능선 2 시작)
    { rect: r(-16, -48, -12, -47), openAtStart: true }, // 2: 방2 입구
    { rect: r(2, -81, 6, -80), openAtStart: false }, // 3: 방2 출구(능선 3 시작)
    { rect: r(2, -92, 6, -91), openAtStart: true }, // 4: 아레나 입구
  ],
  rooms: [
    // 웨이브 순서: 근접 무리 + 후열 주술사 → 방패 벽 + 주술사 증원. 다리 둘 중 어느 쪽으로 건너 후열을 끊을지가 핵심 판단이다.
    { name: '2-1 잿빛 다리목', kind: 'battle', area: r(-18, -38, 18, -14), gateIn: 0, gateOut: 1, backline: r(-16, -37, 16, -29), waves: [{ counts: [10, 0, 0, 2] }, { counts: [8, 4, 0, 3] }] },
    // 멧돼지 돌진(웅덩이에서 끊긴다) → 방패+주술사 포격진 → 혼합 정예. 웅덩이를 가로지를지(화상) 돌아갈지 고른다.
    { name: '2-2 분화구 계단', kind: 'battle', area: r(-24, -80, 8, -48), gateIn: 2, gateOut: 3, backline: r(-22, -79, 6, -72), waves: [{ counts: [8, 0, 3, 0] }, { counts: [0, 4, 0, 4] }, { counts: [12, 2, 2, 2], elites: 1 }] },
    { name: '2-아레나 잿불 대주술사', kind: 'boss', area: r(-14, -124, 22, -92), gateIn: 4, gateOut: -1, waves: [], bossAt: { x: 4, z: -114 }, boss: 'ember' },
  ],
  exits: [],
  spawn: { x: 0, z: 0, facing: N },
  wallHeight: 4.2,
  hazardTheme: 'ember',
  threatClock: true,
  chasms: [
    r(-10, -14, -2, -4), r(2, -14, 10, -4), // 능선 1 양옆
    r(-18, -27, -12, -24), r(-9, -27, 9, -24), r(12, -27, 18, -24), // 방1 용암 골(다리 사이)
    r(-22, -48, -16, -39), r(-12, -48, -6, -39), // 능선 2 양옆
    r(-4, -91, 2, -81), r(6, -91, 12, -81), // 능선 3 양옆
  ],
  lava: [
    r(-12, -67, -6, -61), // 방2 분화구
    r(-10, -100, -6, -96), r(14, -100, 18, -96), // 아레나 남쪽 모서리
  ],
  vents: [
    // 방1: 다리 남쪽 머리. 반 주기 엇갈려 한쪽 다리는 늘 건널 수 있다
    { x: -10.5, z: -21.5, radius: 2.2, room: 0, offset: 0 },
    { x: 10.5, z: -21.5, radius: 2.2, room: 0, offset: 0.5 },
    // 방2: 분화구 둘레 셋, 1/3 주기씩 돈다
    { x: -9, z: -57.5, radius: 2.4, room: 1, offset: 0 },
    { x: -15.5, z: -64, radius: 2.4, room: 1, offset: 1 / 3 },
    { x: -2.5, z: -64, radius: 2.4, room: 1, offset: 2 / 3 },
    // 아레나: 리더 체력 60% 이하에서 깨어난다
    { x: -4, z: -104, radius: 2.6, room: 2, offset: 0, fromBossHp: 0.6 },
    { x: 12, z: -116, radius: 2.6, room: 2, offset: 0.25, fromBossHp: 0.6 },
    { x: 12, z: -104, radius: 2.6, room: 2, offset: 0.5, fromBossHp: 0.6 },
    { x: -4, z: -116, radius: 2.6, room: 2, offset: 0.75, fromBossHp: 0.6 },
  ],
};

// ── R3 서리 묘지(D3a, CONTENT-90D PRD 2.2): 묘지 들판 → 납골 회랑 → 얼어붙은 종루 → 대성당 아레나 ──────
// 북진하다 서쪽으로 비켜 가는 고딕 묘지. 후열(해골 궁수)은 방 북쪽 끝에서 쏜다. 위험: 서리 균열(웅덩이 칸 — 지나가면 동상 피해 + 둔화,
// 몹은 못 들어간다)과 얼음 가시 분출구(경고 원 → 솟구침). 아레나 서리 리치는 체력 60% 아래에서 분출구 넷을 깨운다.
export const R3: MapDef = {
  id: 'r3',
  name: '서리 묘지',
  floors: [
    r(-5, -4, 5, 5), // 묘지 입구
    r(-3, -14, 3, -4), // 회랑 1
    r(-16, -42, 16, -14), // 방1 무덤 들판
    r(-3, -54, 3, -42), // 회랑 2
    r(-18, -82, 10, -54), // 방2 납골 회랑
    r(-13, -94, -7, -82), // 회랑 3
    r(-24, -122, 4, -94), // 방3 얼어붙은 종루
    r(-13, -134, -7, -122), // 회랑 4
    r(-30, -172, 10, -134), // 대성당 아레나
  ],
  landmarks: [
    { kind: 'statue', label: '묘지기 천사상', rect: r(-3, 3, 3, 5), height: 4.4, solid: true },
    // 방1: 무덤 넷(낮은 엄폐)
    { kind: 'rubble', rect: r(-11, -24, -8, -22), height: 1.3, solid: true },
    { kind: 'rubble', rect: r(8, -24, 11, -22), height: 1.3, solid: true },
    { kind: 'rubble', rect: r(-11, -33, -8, -31), height: 1.3, solid: true },
    { kind: 'rubble', rect: r(8, -33, 11, -31), height: 1.3, solid: true },
    // 방2: 납골 기둥
    { kind: 'pillar', rect: r(-15, -60, -13, -58), height: 5, solid: true },
    { kind: 'pillar', rect: r(5, -60, 7, -58), height: 5, solid: true },
    // 방3: 종루 가운데 조각상(돌아 들어가는 동선)
    { kind: 'statue', rect: r(-12, -110, -8, -106), height: 4.2, solid: true },
    // 아레나: 모서리 기둥 넷(리치 뒤 실루엣)
    { kind: 'pillar', rect: r(-27, -140, -25, -138), height: 6.5, solid: true },
    { kind: 'pillar', rect: r(5, -140, 7, -138), height: 6.5, solid: true },
    { kind: 'pillar', rect: r(-27, -169, -25, -167), height: 6.5, solid: true },
    { kind: 'pillar', rect: r(5, -169, 7, -167), height: 6.5, solid: true },
  ],
  gates: [
    { rect: r(-3, -14, 3, -13), openAtStart: true }, // 0: 방1 입구
    { rect: r(-3, -43, 3, -42), openAtStart: false }, // 1: 방1 출구
    { rect: r(-3, -54, 3, -53), openAtStart: true }, // 2: 방2 입구
    { rect: r(-13, -83, -7, -82), openAtStart: false }, // 3: 방2 출구
    { rect: r(-13, -94, -7, -93), openAtStart: true }, // 4: 방3 입구
    { rect: r(-13, -123, -7, -122), openAtStart: false }, // 5: 방3 출구
    { rect: r(-13, -134, -7, -133), openAtStart: true }, // 6: 아레나 입구
  ],
  rooms: [
    // 구울 무리 + 북쪽 끝 해골 궁수 → 방패 해골 벽 + 얼음 망령 돌진. 무덤을 엄폐로 궁수를 먼저 끊는다
    { name: '3-1 무덤 들판', kind: 'battle', area: r(-16, -42, 16, -14), gateIn: 0, gateOut: 1, backline: r(-14, -41, 14, -36), waves: [{ counts: [14, 0, 0, 3] }, { counts: [10, 4, 2, 3], elites: 1 }] },
    // 균열을 사이에 둔 회랑: 망령 돌진이 균열에서 끊긴다
    { name: '3-2 납골 회랑', kind: 'battle', area: r(-18, -82, 10, -54), gateIn: 2, gateOut: 3, backline: r(-16, -81, 8, -76), waves: [{ counts: [12, 3, 0, 2] }, { counts: [8, 2, 4, 3] }, { counts: [14, 4, 2, 3], elites: 1 }] },
    { name: '3-3 얼어붙은 종루', kind: 'battle', area: r(-24, -122, 4, -94), gateIn: 4, gateOut: 5, backline: r(-22, -121, 2, -116), waves: [{ counts: [16, 0, 3, 2] }, { counts: [12, 5, 2, 4], elites: 1 }, { counts: [16, 4, 4, 3], elites: 2 }] },
    { name: '3-아레나 서리 리치', kind: 'boss', area: r(-30, -172, 10, -134), gateIn: 6, gateOut: -1, waves: [], bossAt: { x: -10, z: -162 }, boss: 'lich' },
  ],
  exits: [],
  spawn: { x: 0, z: 0, facing: N },
  wallHeight: 3.6,
  hazardTheme: 'frost',
  threatClock: true,
  lava: [
    r(-8, -71, -2, -65), // 방2 가운데 균열
    r(-14, -103, -10, -99), // 방3 남쪽 균열
    r(-25, -149, -21, -145), r(1, -149, 5, -145), // 아레나 남쪽 모서리
  ],
  vents: [
    { x: -9, z: -27.5, radius: 2.2, room: 0, offset: 0 },
    { x: 9, z: -27.5, radius: 2.2, room: 0, offset: 0.5 },
    { x: -13, z: -68, radius: 2.4, room: 1, offset: 0 },
    { x: 4, z: -68, radius: 2.4, room: 1, offset: 1 / 3 },
    { x: -5, z: -60, radius: 2.4, room: 1, offset: 2 / 3 },
    { x: -19, z: -104, radius: 2.4, room: 2, offset: 0 },
    { x: -1, z: -112, radius: 2.4, room: 2, offset: 0.5 },
    { x: -20, z: -158, radius: 2.6, room: 3, offset: 0, fromBossHp: 0.6 },
    { x: 0, z: -158, radius: 2.6, room: 3, offset: 0.25, fromBossHp: 0.6 },
    { x: 0, z: -146, radius: 2.6, room: 3, offset: 0.5, fromBossHp: 0.6 },
    { x: -20, z: -146, radius: 2.6, room: 3, offset: 0.75, fromBossHp: 0.6 },
  ],
};

// ── R4 가라앉은 사원(D3a): 잠긴 회랑 → 동쪽 침수 정원 → 기도실 → 히드라 못 ──────
// 동쪽으로 꺾였다 돌아오는 사원 둑길. 위험: 독 늪(웅덩이 칸 — 들어서면 중독, 몹은 못 들어간다)과 독가스 분출구.
// 늪의 히드라는 체력 60% 아래에서 못 둘레 분출구 넷을 깨운다.
export const R4: MapDef = {
  id: 'r4',
  name: '가라앉은 사원',
  floors: [
    r(-5, -4, 5, 5), // 사원 둑 입구
    r(-2, -14, 2, -4), // 둑길 1
    r(-16, -40, 16, -14), // 방1 잠긴 회랑
    r(10, -52, 14, -40), // 둑길 2(동쪽)
    r(-4, -84, 28, -52), // 방2 침수 정원
    r(2, -96, 6, -84), // 둑길 3
    r(-14, -126, 18, -96), // 방3 기도실
    r(-2, -138, 2, -126), // 둑길 4
    r(-20, -176, 20, -138), // 히드라 못
  ],
  landmarks: [
    { kind: 'statue', label: '가라앉은 우상', rect: r(-3, 3, 3, 5), height: 4.6, solid: true },
    { kind: 'pillar', rect: r(-14, -18, -12, -16), height: 5, solid: true },
    { kind: 'pillar', rect: r(12, -18, 14, -16), height: 5, solid: true },
    { kind: 'pillar', rect: r(0, -58, 2, -56), height: 5.5, solid: true },
    { kind: 'pillar', rect: r(22, -58, 24, -56), height: 5.5, solid: true },
    { kind: 'rubble', rect: r(-1, -76, 2, -74), height: 1.2, solid: true },
    { kind: 'pillar', rect: r(-12, -100, -10, -98), height: 5.5, solid: true },
    { kind: 'pillar', rect: r(14, -100, 16, -98), height: 5.5, solid: true },
    { kind: 'pillar', rect: r(-18, -142, -16, -140), height: 7, solid: true },
    { kind: 'pillar', rect: r(16, -142, 18, -140), height: 7, solid: true },
    { kind: 'pillar', rect: r(-18, -174, -16, -172), height: 7, solid: true },
    { kind: 'pillar', rect: r(16, -174, 18, -172), height: 7, solid: true },
  ],
  gates: [
    { rect: r(-2, -14, 2, -13), openAtStart: true }, // 0: 방1 입구
    { rect: r(10, -41, 14, -40), openAtStart: false }, // 1: 방1 출구(동쪽 둑길)
    { rect: r(10, -52, 14, -51), openAtStart: true }, // 2: 방2 입구
    { rect: r(2, -85, 6, -84), openAtStart: false }, // 3: 방2 출구
    { rect: r(2, -96, 6, -95), openAtStart: true }, // 4: 방3 입구
    { rect: r(-2, -127, 2, -126), openAtStart: false }, // 5: 방3 출구
    { rect: r(-2, -138, 2, -137), openAtStart: true }, // 6: 못 입구
  ],
  rooms: [
    { name: '4-1 잠긴 회랑', kind: 'battle', area: r(-16, -40, 16, -14), gateIn: 0, gateOut: 1, backline: r(-14, -39, 14, -34), waves: [{ counts: [12, 2, 0, 2] }, { counts: [10, 3, 3, 3], elites: 1 }] },
    { name: '4-2 침수 정원', kind: 'battle', area: r(-4, -84, 28, -52), gateIn: 2, gateOut: 3, backline: r(-2, -83, 26, -79), waves: [{ counts: [10, 4, 3, 2] }, { counts: [6, 4, 0, 4] }, { counts: [14, 3, 3, 3], elites: 1 }] },
    { name: '4-3 기도실', kind: 'battle', area: r(-14, -126, 18, -96), gateIn: 4, gateOut: 5, backline: r(-12, -125, 16, -120), waves: [{ counts: [16, 2, 3, 2] }, { counts: [12, 5, 3, 4], elites: 1 }, { counts: [16, 4, 4, 4], elites: 2 }] },
    { name: '4-못 늪의 히드라', kind: 'boss', area: r(-20, -176, 20, -138), gateIn: 6, gateOut: -1, waves: [], bossAt: { x: 0, z: -162 }, boss: 'hydra' },
  ],
  exits: [],
  spawn: { x: 0, z: 0, facing: N },
  wallHeight: 3.8,
  hazardTheme: 'venom',
  threatClock: true,
  lava: [
    r(-12, -25, -7, -21), r(7, -35, 12, -31), // 방1 고인 늪
    r(6, -72, 14, -64), // 방2 가운데 침수 구덩이
    r(-8, -115, -3, -110), r(9, -111, 14, -106), // 방3
    r(-16, -151, -11, -146), r(11, -151, 16, -146), // 못 남쪽 모서리
  ],
  vents: [
    { x: -8, z: -33.5, radius: 2.2, room: 0, offset: 0 },
    { x: 8, z: -20.5, radius: 2.2, room: 0, offset: 0.5 },
    { x: 2, z: -61, radius: 2.4, room: 1, offset: 0 },
    { x: 20, z: -61, radius: 2.4, room: 1, offset: 1 / 3 },
    { x: 20, z: -75, radius: 2.4, room: 1, offset: 2 / 3 },
    { x: -6, z: -103, radius: 2.4, room: 2, offset: 0 },
    { x: 10, z: -117, radius: 2.4, room: 2, offset: 0.5 },
    { x: -11, z: -158, radius: 2.6, room: 3, offset: 0, fromBossHp: 0.6 },
    { x: 11, z: -158, radius: 2.6, room: 3, offset: 0.25, fromBossHp: 0.6 },
    { x: 11, z: -168, radius: 2.6, room: 3, offset: 0.5, fromBossHp: 0.6 },
    { x: -11, z: -168, radius: 2.6, room: 3, offset: 0.75, fromBossHp: 0.6 },
  ],
};

// ── R5 황금 모래 폐도(3개월 C2): 열주 대로 → 서쪽 가라앉은 안뜰 → 오벨리스크 광장 → 모래 군주의 왕좌 ──────
// 서쪽으로 꺾여 내려가는 사막 고도. 위험: 유사(웅덩이 칸 — 빠지면 둔화 + 모래 찰과상, 몹은 못 들어간다)와 모래 간헐천(분출구).
// 모래 군주는 체력 60% 아래에서 왕좌 둘레 간헐천 넷을 깨운다.
export const R5: MapDef = {
  id: 'r5',
  name: '황금 모래 폐도',
  floors: [
    r(-5, -4, 5, 5), // 성문 앞
    r(-3, -14, 3, -4), // 대로 입구
    r(-12, -48, 12, -14), // 방1 열주 대로
    r(-12, -58, -8, -48), // 서쪽 계단
    r(-34, -88, -2, -58), // 방2 가라앉은 안뜰
    r(-20, -100, -16, -88), // 회랑 3
    r(-36, -130, 4, -100), // 방3 오벨리스크 광장
    r(-18, -142, -14, -130), // 회랑 4
    r(-38, -182, 6, -142), // 왕좌 아레나
  ],
  landmarks: [
    { kind: 'statue', label: '자칼 수호상', rect: r(-3, 3, 3, 5), height: 4.8, solid: true },
    // 방1: 열주(대로 양옆 기둥 줄 — 원거리 사선을 끊는 엄폐)
    { kind: 'pillar', rect: r(-9, -21, -7, -19), height: 6, solid: true },
    { kind: 'pillar', rect: r(7, -21, 9, -19), height: 6, solid: true },
    { kind: 'pillar', rect: r(-9, -29, -7, -27), height: 6, solid: true },
    { kind: 'pillar', rect: r(7, -29, 9, -27), height: 6, solid: true },
    { kind: 'pillar', rect: r(-9, -37, -7, -35), height: 6, solid: true },
    { kind: 'pillar', rect: r(7, -37, 9, -35), height: 6, solid: true },
    { kind: 'pillar', rect: r(-9, -45, -7, -43), height: 6, solid: true },
    { kind: 'pillar', rect: r(7, -45, 9, -43), height: 6, solid: true },
    // 방2: 무너진 담 조각
    { kind: 'rubble', rect: r(-31, -64, -28, -62), height: 1.4, solid: true },
    { kind: 'rubble', rect: r(-8, -85, -5, -83), height: 1.4, solid: true },
    // 방3: 오벨리스크 넷 + 가운데 스핑크스 좌상(돌아 들어가는 동선)
    { kind: 'pillar', rect: r(-27, -109, -25, -107), height: 7.5, solid: true },
    { kind: 'pillar', rect: r(-7, -109, -5, -107), height: 7.5, solid: true },
    { kind: 'pillar', rect: r(-27, -123, -25, -121), height: 7.5, solid: true },
    { kind: 'pillar', rect: r(-7, -123, -5, -121), height: 7.5, solid: true },
    { kind: 'statue', rect: r(-18, -117, -14, -113), height: 4.2, solid: true },
    // 아레나: 모서리 오벨리스크 넷
    { kind: 'pillar', rect: r(-36, -146, -34, -144), height: 7, solid: true },
    { kind: 'pillar', rect: r(2, -146, 4, -144), height: 7, solid: true },
    { kind: 'pillar', rect: r(-36, -180, -34, -178), height: 7, solid: true },
    { kind: 'pillar', rect: r(2, -180, 4, -178), height: 7, solid: true },
  ],
  gates: [
    { rect: r(-3, -14, 3, -13), openAtStart: true }, // 0: 방1 입구
    { rect: r(-12, -49, -8, -48), openAtStart: false }, // 1: 방1 출구(서쪽 계단)
    { rect: r(-12, -58, -8, -57), openAtStart: true }, // 2: 방2 입구
    { rect: r(-20, -89, -16, -88), openAtStart: false }, // 3: 방2 출구
    { rect: r(-20, -100, -16, -99), openAtStart: true }, // 4: 방3 입구
    { rect: r(-18, -131, -14, -130), openAtStart: false }, // 5: 방3 출구
    { rect: r(-18, -142, -14, -141), openAtStart: true }, // 6: 아레나 입구
  ],
  rooms: [
    // 미라 병사 무리 + 대로 끝 자칼 사제 → 스카라브 방패 벽 + 전갈 돌진. 열주를 끼고 사제를 먼저 끊는다
    { name: '5-1 열주 대로', kind: 'battle', area: r(-12, -48, 12, -14), gateIn: 0, gateOut: 1, backline: r(-10, -47, 10, -42), waves: [{ counts: [14, 2, 0, 3] }, { counts: [10, 4, 3, 3], elites: 1 }] },
    // 가운데 유사 구덩이를 사이에 둔 안뜰: 전갈 돌진이 구덩이에서 끊긴다
    { name: '5-2 가라앉은 안뜰', kind: 'battle', area: r(-34, -88, -2, -58), gateIn: 2, gateOut: 3, backline: r(-32, -87, -4, -82), waves: [{ counts: [12, 3, 3, 3] }, { counts: [8, 4, 0, 5] }, { counts: [16, 4, 4, 3], elites: 1 }] },
    { name: '5-3 오벨리스크 광장', kind: 'battle', area: r(-36, -130, 4, -100), gateIn: 4, gateOut: 5, backline: r(-34, -129, 2, -124), waves: [{ counts: [18, 2, 3, 3] }, { counts: [12, 6, 3, 4], elites: 1 }, { counts: [18, 4, 5, 4], elites: 2 }] },
    { name: '5-왕좌 모래 군주', kind: 'boss', area: r(-38, -182, 6, -142), gateIn: 6, gateOut: -1, waves: [], bossAt: { x: -16, z: -168 }, boss: 'sandlord' },
  ],
  exits: [],
  spawn: { x: 0, z: 0, facing: N },
  wallHeight: 4.0,
  hazardTheme: 'sand',
  threatClock: true,
  lava: [
    r(-4, -34, 4, -30), // 방1 대로 가운데 꺼진 유사
    r(-22, -77, -14, -69), // 방2 마른 분수 유사 구덩이
    r(-33, -118, -29, -114), r(-4, -114, 0, -110), // 방3
    r(-37, -160, -32, -155), r(1, -160, 6, -155), // 아레나 남쪽 모서리
  ],
  vents: [
    { x: -4, z: -24, radius: 2.2, room: 0, offset: 0 },
    { x: 4, z: -40, radius: 2.2, room: 0, offset: 0.5 },
    { x: -28, z: -70, radius: 2.4, room: 1, offset: 0 },
    { x: -8, z: -70, radius: 2.4, room: 1, offset: 1 / 3 },
    { x: -18, z: -84, radius: 2.4, room: 1, offset: 2 / 3 },
    { x: -31, z: -104, radius: 2.4, room: 2, offset: 0 },
    { x: -1, z: -126, radius: 2.4, room: 2, offset: 0.5 },
    { x: -27, z: -152, radius: 2.6, room: 3, offset: 0, fromBossHp: 0.6 },
    { x: -5, z: -152, radius: 2.6, room: 3, offset: 0.25, fromBossHp: 0.6 },
    { x: -5, z: -174, radius: 2.6, room: 3, offset: 0.5, fromBossHp: 0.6 },
    { x: -27, z: -174, radius: 2.6, room: 3, offset: 0.75, fromBossHp: 0.6 },
  ],
};

// ── R6 심연의 성채(3개월 C2, 스토리 엔딩): 성벽 위 → 동쪽 부서진 탑 → 처형장 → 심연의 군주(정상) ──────
// 허공 위 흑요석 성채. 좁은 성벽에서 시작해 동쪽으로 오르며 넓어진다. 위험: 심연 균열(웅덩이 칸 — 들어서면 침식, 몹은 못 들어간다)과 공허 기둥(분출구).
// 심연의 군주는 체력 60% 아래에서 정상 둘레 공허 기둥 넷을 깨운다.
export const R6: MapDef = {
  id: 'r6',
  name: '심연의 성채',
  floors: [
    r(-5, -4, 5, 5), // 성채 교두보
    r(-2, -16, 2, -4), // 사슬 다리
    r(-8, -50, 8, -16), // 방1 성벽 위
    r(4, -60, 8, -50), // 계단 2(동쪽)
    r(-6, -94, 30, -60), // 방2 부서진 탑
    r(20, -108, 24, -94), // 회랑 3
    r(4, -140, 40, -108), // 방3 처형장
    r(20, -152, 24, -140), // 회랑 4
    r(0, -196, 44, -152), // 정상 아레나
  ],
  landmarks: [
    { kind: 'statue', label: '심연의 파수상', rect: r(-3, 3, 3, 5), height: 5, solid: true },
    // 방1: 성가퀴(좁은 성벽의 엄폐)
    { kind: 'pillar', rect: r(-8, -27, -6, -25), height: 4, solid: true },
    { kind: 'pillar', rect: r(6, -37, 8, -35), height: 4, solid: true },
    // 방2: 무너진 탑 기둥
    { kind: 'pillar', rect: r(2, -70, 4, -68), height: 6, solid: true },
    { kind: 'pillar', rect: r(22, -70, 24, -68), height: 6, solid: true },
    { kind: 'rubble', rect: r(10, -87, 13, -85), height: 1.4, solid: true },
    // 방3: 가운데 처형대 + 모서리 고문 기둥
    { kind: 'altar', label: '처형대', rect: r(20, -126, 24, -122), height: 1.4, solid: true },
    { kind: 'pillar', rect: r(6, -112, 8, -110), height: 6, solid: true },
    { kind: 'pillar', rect: r(36, -112, 38, -110), height: 6, solid: true },
    { kind: 'pillar', rect: r(6, -138, 8, -136), height: 6, solid: true },
    { kind: 'pillar', rect: r(36, -138, 38, -136), height: 6, solid: true },
    // 정상: 모서리 공허 불꽃 기둥 넷
    { kind: 'pillar', rect: r(2, -156, 4, -154), height: 8, solid: true },
    { kind: 'pillar', rect: r(40, -156, 42, -154), height: 8, solid: true },
    { kind: 'pillar', rect: r(2, -194, 4, -192), height: 8, solid: true },
    { kind: 'pillar', rect: r(40, -194, 42, -192), height: 8, solid: true },
  ],
  gates: [
    { rect: r(-2, -16, 2, -15), openAtStart: true }, // 0: 방1 입구
    { rect: r(4, -51, 8, -50), openAtStart: false }, // 1: 방1 출구(동쪽 계단)
    { rect: r(4, -60, 8, -59), openAtStart: true }, // 2: 방2 입구
    { rect: r(20, -95, 24, -94), openAtStart: false }, // 3: 방2 출구
    { rect: r(20, -108, 24, -107), openAtStart: true }, // 4: 방3 입구
    { rect: r(20, -141, 24, -140), openAtStart: false }, // 5: 방3 출구
    { rect: r(20, -152, 24, -151), openAtStart: true }, // 6: 정상 입구
  ],
  rooms: [
    // 좁은 성벽: 악마 기사가 앞을 막고 심연 주술사가 뒤에서 쏜다. 균열 양옆 좁은 길로 밀고 나간다
    { name: '6-1 성벽 위', kind: 'battle', area: r(-8, -50, 8, -16), gateIn: 0, gateOut: 1, backline: r(-6, -49, 6, -44), waves: [{ counts: [12, 3, 0, 3] }, { counts: [10, 4, 3, 4], elites: 1 }] },
    { name: '6-2 부서진 탑', kind: 'battle', area: r(-6, -94, 30, -60), gateIn: 2, gateOut: 3, backline: r(-4, -93, 28, -88), waves: [{ counts: [12, 4, 4, 3] }, { counts: [8, 4, 0, 6] }, { counts: [16, 5, 4, 4], elites: 2 }] },
    { name: '6-3 처형장', kind: 'battle', area: r(4, -140, 40, -108), gateIn: 4, gateOut: 5, backline: r(6, -139, 38, -134), waves: [{ counts: [18, 3, 4, 3] }, { counts: [12, 6, 4, 5], elites: 1 }, { counts: [18, 5, 5, 5], elites: 2 }] },
    { name: '6-정상 심연의 군주', kind: 'boss', area: r(0, -196, 44, -152), gateIn: 6, gateOut: -1, waves: [], bossAt: { x: 22, z: -180 }, boss: 'abysslord' },
  ],
  exits: [],
  spawn: { x: 0, z: 0, facing: N },
  wallHeight: 4.4,
  hazardTheme: 'void',
  threatClock: true,
  lava: [
    r(-3, -34, 3, -30), // 방1 성벽 가운데 갈라진 틈
    r(4, -84, 10, -76), r(16, -78, 24, -72), // 방2
    r(10, -120, 15, -115), r(29, -133, 34, -128), // 방3
    r(6, -166, 11, -161), r(33, -166, 38, -161), r(6, -188, 11, -183), r(33, -188, 38, -183), // 정상 네 모서리
  ],
  vents: [
    { x: -4, z: -22, radius: 2.2, room: 0, offset: 0 },
    { x: 3, z: -42, radius: 2.2, room: 0, offset: 0.5 },
    { x: 0, z: -66, radius: 2.4, room: 1, offset: 0 },
    { x: 26, z: -66, radius: 2.4, room: 1, offset: 1 / 3 },
    { x: 14, z: -90, radius: 2.4, room: 1, offset: 2 / 3 },
    { x: 12, z: -130, radius: 2.4, room: 2, offset: 0 },
    { x: 32, z: -114, radius: 2.4, room: 2, offset: 0.5 },
    { x: 12, z: -176, radius: 2.6, room: 3, offset: 0, fromBossHp: 0.6 },
    { x: 22, z: -162, radius: 2.6, room: 3, offset: 0.25, fromBossHp: 0.6 },
    { x: 32, z: -176, radius: 2.6, room: 3, offset: 0.5, fromBossHp: 0.6 },
    { x: 22, z: -190, radius: 2.6, room: 3, offset: 0.75, fromBossHp: 0.6 },
  ],
};

export const MAPS: { readonly village: MapDef } & { readonly [K in RegionId]: MapDef } = { village: VILLAGE, r1: R1, r2: R2, r3: R3, r4: R4, r5: R5, r6: R6 };

// ── 마을 ①~④(S7, STAGE-WORLD-PRD 5.1): 마을①은 VILLAGE, 마을②~④는 각자 지도(shared/villageMaps.ts — 잿불 전초기지·서리 항구·늪지 장터) ──
// 마을 n의 던전 문 = [지역 n(지금 지역), 지역 n+1(다음 지역 — n-10 첫 클리어로 열린다)]. 시작 자리는 같고, 문 자리·방향은 마을마다 다르다(STAGE-WORLD-PRD 5.4).
// 지도 id는 모두 'village'. 판정 격자는 지도 객체마다 따로다(마을①은 game.grids.village, ②~④는 stageMaps.gridOfMap).
/** 마을 번호(1..4) 순서. 정본 이름은 content/world.ts TOWNS_90D와 같다(content.test가 확인) */
export const VILLAGES: readonly MapDef[] = [VILLAGE, ...TOWN_MAPS];
/** 플레이할 수 있는 마을 수(지도가 있는 마을) */
export const TOWN_COUNT = VILLAGES.length;
export function villageOf(townNo: number): MapDef {
  return VILLAGES[Math.max(1, Math.min(TOWN_COUNT, Math.floor(townNo) || 1)) - 1];
}

// ── 격자 래스터화 ────────────────────────────────────
export const Cell = { Void: 0, Floor: 1, Solid: 2 } as const;

export interface MapGrid {
  /** 셀 (0,0)의 월드 x,z(최소 모서리) */
  readonly originX: number;
  readonly originZ: number;
  readonly width: number;
  readonly height: number;
  /** 셀 크기 1m */
  readonly cell: 1;
  /** Cell 값. 문은 Floor로 들어 있고, 닫힘 여부는 FlowView.gateOpen과 gateCells로 판단한다 */
  readonly cells: Uint8Array;
  /** 셀마다 속한 문 인덱스 + 1(0 = 문 아님) */
  readonly gateCells: Uint8Array;
  /** 셀마다 용암 웅덩이 여부(1). cells에는 Floor로 들어 있다(영웅은 걷고, 몹·보스는 막힌다) */
  readonly lavaCells: Uint8Array;
}

const MARGIN = 3;

export function rasterize(map: MapDef): MapGrid {
  let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
  for (const f of map.floors) {
    minX = Math.min(minX, f.x0); minZ = Math.min(minZ, f.z0);
    maxX = Math.max(maxX, f.x1); maxZ = Math.max(maxZ, f.z1);
  }
  for (const l of map.landmarks) {
    minX = Math.min(minX, l.rect.x0); minZ = Math.min(minZ, l.rect.z0);
    maxX = Math.max(maxX, l.rect.x1); maxZ = Math.max(maxZ, l.rect.z1);
  }
  const originX = minX - MARGIN;
  const originZ = minZ - MARGIN;
  const width = maxX - minX + MARGIN * 2;
  const height = maxZ - minZ + MARGIN * 2;
  const cells = new Uint8Array(width * height);
  const gateCells = new Uint8Array(width * height);
  const lavaCells = new Uint8Array(width * height);
  const fill = (rc: Rect, value: number, target: Uint8Array) => {
    for (let z = rc.z0; z < rc.z1; z++) {
      for (let x = rc.x0; x < rc.x1; x++) target[(z - originZ) * width + (x - originX)] = value;
    }
  };
  for (const f of map.floors) fill(f, Cell.Floor, cells);
  for (const l of map.landmarks) if (l.solid) fill(l.rect, Cell.Solid, cells);
  map.gates.forEach((g, i) => fill(g.rect, i + 1, gateCells));
  for (const l of map.lava ?? []) fill(l, 1, lavaCells);
  return { originX, originZ, width, height, cell: 1, cells, gateCells, lavaCells };
}

/** 월드 좌표가 용암 웅덩이인지 */
export function lavaAt(grid: MapGrid, x: number, z: number): boolean {
  const cx = Math.floor(x - grid.originX);
  const cz = Math.floor(z - grid.originZ);
  if (cx < 0 || cz < 0 || cx >= grid.width || cz >= grid.height) return false;
  return grid.lavaCells[cz * grid.width + cx] === 1;
}

/** 월드 좌표의 셀 값(격자 밖은 Void) */
export function cellAt(grid: MapGrid, x: number, z: number): number {
  const cx = Math.floor(x - grid.originX);
  const cz = Math.floor(z - grid.originZ);
  if (cx < 0 || cz < 0 || cx >= grid.width || cz >= grid.height) return Cell.Void;
  return grid.cells[cz * grid.width + cx];
}

/** 월드 좌표의 문 인덱스(-1 = 문 아님) */
export function gateAt(grid: MapGrid, x: number, z: number): number {
  const cx = Math.floor(x - grid.originX);
  const cz = Math.floor(z - grid.originZ);
  if (cx < 0 || cz < 0 || cx >= grid.width || cz >= grid.height) return -1;
  return grid.gateCells[cz * grid.width + cx] - 1;
}

export function inRect(rc: Rect, x: number, z: number, inset = 0): boolean {
  return x >= rc.x0 + inset && x <= rc.x1 - inset && z >= rc.z0 + inset && z <= rc.z1 - inset;
}
